import { type FC, type PropsWithChildren, createContext, useCallback, useEffect, useRef } from 'react';

import { fetchFeaturesAPI } from '@vakantiesnl/services/src/hooks/rest/feature-toggle';

import { type ApplicationState, type ApplicationStore, createApplicationStore } from './applicationStore';

export const ApplicationStoreContext = createContext<ApplicationStore | null>(null);

type Props = PropsWithChildren<Partial<ApplicationState> & { enableFeatures?: boolean }>;

export const ApplicationStoreProvider: FC<Props> = ({
	children,
	isBot,
	staticCanonicalOrigin,
	dynamicOrigin,
	userId,
	enableFeatures = true,
}) => {
	const store = useRef<ApplicationStore>();

	if (!store.current) {
		store.current = createApplicationStore({ isBot, staticCanonicalOrigin, dynamicOrigin, userId });
	}

	// Update the store in case any of the props have changed upon navigation.
	useEffect(() => {
		if (store.current && isBot !== undefined) {
			store.current.setState({ isBot });
		}
	}, [isBot]);

	useEffect(() => {
		if (store.current && staticCanonicalOrigin !== undefined) {
			store.current.setState({ staticCanonicalOrigin });
		}
	}, [staticCanonicalOrigin]);

	useEffect(() => {
		if (store.current && dynamicOrigin !== undefined) {
			store.current.setState({ dynamicOrigin });
		}
	}, [dynamicOrigin]);

	const fetchFeatures = useCallback(async () => {
		if (store.current && enableFeatures) {
			try {
				const features = await fetchFeaturesAPI(window.origin);
				if (features) store.current.setState({ features });
			} catch (err) {}
		}
	}, [enableFeatures]);

	/** Fetch features on mount when a sessionId is provided */
	useEffect(() => {
		if (store.current) {
			fetchFeatures();
		}
	}, [fetchFeatures]);

	return <ApplicationStoreContext.Provider value={store.current}>{children}</ApplicationStoreContext.Provider>;
};
